import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import * as moment from 'moment';
import { DualAxes } from '@antv/g2plot';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { CustomerService } from '../../services/customer.service';
import { WarrantyService } from '../../services/warranty.service';

interface SummaryCard {
  label: string;
  value: string;
  bgColor: string;
}

interface WarrantyDetailRow {
  claimNumber: string;
  submitDate: string;
  qty: number;
  materialNumber: string;
  claimAmount: string;
  remark1: string;
  remark2: string;
  status: string;
  statusName: string;
}

interface CategoryAnalysisRow {
  category: string;
  claimQty: number;
  claimAmount: string;
  purchaseQty: number;
  purchaseAmount: string;
  amountRatio: string;
}

interface MonthlyOverviewPoint {
  month: string;
  qty: number;
  amount: number;
}

interface TablePagination {
  total: number;
  pageIndex: number;
  pageSize: number;
  pageSizeOptions: number[];
}

@Component({
  selector: 'warranty',
  templateUrl: './warranty.component.html',
  styleUrls: ['./warranty.component.less'],
})
export class WarrantyComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('monthlyChart') monthlyChartRef!: ElementRef<HTMLDivElement>;
  private monthlyChartInstance: DualAxes | null = null;
  private customerSubscription?: Subscription;

  dateRange: Date[] = [];
  ranges: Record<string, Date[]> = {};
  lastUpdateTime = '';

  /** 质保申请明细查询条件 */
  detailFilter: { claimNo: string; boschMaterialNo: string; dateRange: Date[] } = {
    claimNo: '',
    boschMaterialNo: '',
    dateRange: [],
  };

  summaryCards: SummaryCard[] = [
    { label: '已申请索赔单数', value: '-', bgColor: '#9E2896' },
    { label: '已批准索赔单数', value: '-', bgColor: '#007BC0' },
    { label: '已批准索赔单总额', value: '-', bgColor: '#00884A' },
    { label: '已批准索赔金额占比', value: '-', bgColor: '#18817D' },
  ];

  constructor(
    private warrantyService: WarrantyService,
    private customerService: CustomerService,
  ) {}

  monthlyOverview: MonthlyOverviewPoint[] = [];

  categoryAnalysis: CategoryAnalysisRow[] = [];

  warrantyDetails: WarrantyDetailRow[] = [];

  newsList: Array<{
    date: string;
    tag: string;
    tagColor: string;
    tagBg: string;
    title: string;
    desc: string;
  }> = [];

  pagination = {
    total: 0,
    pageIndex: 1,
    pageSize: 6,
    pageSizeOptions: [6],
  };

  categoryPagination: TablePagination = {
    total: 0,
    pageIndex: 1,
    pageSize: 6,
    pageSizeOptions: [6],
  };

  ngOnInit(): void {
    const startOfMonth = moment().startOf('month').toDate();
    const endOfMonth = moment().endOf('month').toDate();
    const startOfLastMonth = moment().subtract(1, 'month').startOf('month').toDate();
    const endOfLastMonth = moment().subtract(1, 'month').endOf('month').toDate();
    const startOfLastThreeMonths = moment().subtract(3, 'months').startOf('month').toDate();
    /** 默认区间：当前月往前推一年所在月份 ~ 当前月 */
    const startOfLastYear = moment().subtract(1, 'year').startOf('month').toDate();
    this.dateRange = [startOfLastYear, endOfMonth];
    this.detailFilter.dateRange = [...this.dateRange];
    this.ranges = {
      '本月': [startOfMonth, endOfMonth],
      '上月': [startOfLastMonth, endOfLastMonth],
      '近三月': [startOfLastThreeMonths, endOfMonth],
      '本年': [moment().startOf('year').toDate(), moment().endOf('year').toDate()],
    };
    this.pagination.total = 0;
    this.customerSubscription = this.customerService.selectedCustomer$
      .pipe(skip(1))
      .subscribe(() => this.handleCustomerChange());

    const selectedCustomer = this.customerService.getSelectedCustomer();
    const currentCountry = localStorage.getItem('country');
    if (selectedCustomer && selectedCustomer.country === currentCountry) {
      this.refreshWarrantyData();
    }
  }

  /** 调用 summary 接口，刷新顶部汇总卡片与数据更新时间 */
  fetchSummary(): void {
    if (!this.dateRange || this.dateRange.length !== 2) {
      return;
    }
    const params = {
      startDate: moment(this.dateRange[0]).format('YYYY-MM'),
      endDate: moment(this.dateRange[1]).format('YYYY-MM'),
    };
    this.warrantyService.apiSummary(params).subscribe({
      next: (res) => {
        if (res.rtnData) {
          const d = res.rtnData;
          this.lastUpdateTime = d.dataUpdateTime || '';
          this.summaryCards = [
            { label: '已申请索赔单数', value: d.applyCount != null ? `${d.applyCount}` : '-', bgColor: '#9E2896' },
            { label: '已批准索赔单数', value: d.claimTotalCount != null ? `${d.claimTotalCount}` : '-', bgColor: '#007BC0' },
            { label: '已批准索赔单总额', value: d.claimTotalAmount != null ? `${this.formatAmount(d.claimTotalAmount)} CNY` : '-', bgColor: '#00884A' },
            { label: '已批准索赔金额占比', value: d.claimAmountRatio != null ? `${d.claimAmountRatio}%` : '-', bgColor: '#18817D' },
          ];
        }
      },
    });
  }

  get pagedCategoryAnalysis(): CategoryAnalysisRow[] {
    return this.getPagedRows(this.categoryAnalysis, this.categoryPagination);
  }

  get pagedWarrantyDetails(): WarrantyDetailRow[] {
    return this.warrantyDetails;
  }

  private getPagedRows<T>(rows: T[], pagination: TablePagination): T[] {
    const start = (pagination.pageIndex - 1) * pagination.pageSize;
    return rows.slice(start, start + pagination.pageSize);
  }

  /** 千分位格式化，如 12345.6 → 12,345.6；非数值原样返回 */
  private formatAmount(value: number | string): string {
    const num = Number(value);
    return Number.isFinite(num) ? num.toLocaleString('en-US') : `${value}`;
  }

  ngAfterViewInit(): void {
    this.renderMonthlyChart();
  }

  ngOnDestroy(): void {
    this.monthlyChartInstance?.destroy();
    this.customerSubscription?.unsubscribe();
  }

  onDateRangeChange(): void {
    this.pagination.pageIndex = 1;
    this.categoryPagination.pageIndex = 1;
    this.detailFilter.dateRange = this.dateRange ? [...this.dateRange] : [];
    this.refreshWarrantyData();
  }

  /** 明细查询：应用筛选条件并重置到第一页 */
  handleDetailSearch(): void {
    this.pagination.pageIndex = 1;
    this.fetchWarrantyDetails();
  }

  /** 明细清除：重置筛选条件为默认（日期回到全局区间） */
  handleDetailClear(): void {
    this.detailFilter.claimNo = '';
    this.detailFilter.boschMaterialNo = '';
    this.detailFilter.dateRange = this.dateRange ? [...this.dateRange] : [];
    this.pagination.pageIndex = 1;
    this.fetchWarrantyDetails();
  }

  /** 区间选择中已选中的起始月份，用于限制跨度不超过 18 个月 */
  private pickingStart: Date | null = null;

  /** 记录面板中先选中的起点月份 */
  onCalendarChange(dates: (Date | null)[]): void {
    this.pickingStart = dates && dates.length === 1 && dates[0] ? dates[0] : null;
  }

  /** 面板关闭时重置起点 */
  onOpenChange(open: boolean): void {
    if (!open) {
      this.pickingStart = null;
    }
  }

  /** 月份禁用：与已选起点相距超过 18 个月的不可选 */
  disabledMonth = (current: Date): boolean => {
    if (!this.pickingStart) {
      return false;
    }
    const diffMonths = Math.abs(moment(current).startOf('month').diff(moment(this.pickingStart).startOf('month'), 'months'));
    return diffMonths > 17;
  };

  private handleCustomerChange(): void {
    this.pagination.pageIndex = 1;
    this.categoryPagination.pageIndex = 1;
    this.refreshWarrantyData();
  }

  private refreshWarrantyData(): void {
    this.fetchSummary();
    this.fetchMonthlyOverview();
    this.fetchProductCategoryAnalysis();
    this.fetchWarrantyDetails();
    this.fetchNews();
  }

  /** 调用 monthly-overview 接口，刷新月度索赔总览图表 */
  fetchMonthlyOverview(): void {
    if (!this.dateRange || this.dateRange.length !== 2) {
      return;
    }
    const params = {
      startDate: moment(this.dateRange[0]).startOf('month').format('YYYY-MM'),
      endDate: moment(this.dateRange[1]).endOf('month').format('YYYY-MM'),
    };
    this.warrantyService.apiMonthlyOverview(params).subscribe({
      next: (res) => {
        if (Array.isArray(res.rtnData)) {
          this.monthlyOverview = res.rtnData.map((item: any) => ({
            month: item.month || '',
            qty: item.claimCount ?? 0,
            amount: item.claimAmount ?? 0,
          }));
          this.renderMonthlyChart();
        }
      },
    });
  }

  /** 调用 product-category-analysis 接口，刷新索赔金额和比例分析表格 */
  fetchProductCategoryAnalysis(): void {
    if (!this.dateRange || this.dateRange.length !== 2) {
      return;
    }
    const params = {
      startDate: moment(this.dateRange[0]).startOf('month').format('YYYY-MM'),
      endDate: moment(this.dateRange[1]).endOf('month').format('YYYY-MM'),
    };
    this.warrantyService.apiProductCategoryAnalysis(params).subscribe({
      next: (res) => {
        if (Array.isArray(res.rtnData)) {
          this.categoryAnalysis = res.rtnData.map((item: any) => ({
            category: item.productCategory || '',
            claimQty: item.warrantyClaimQuantity ?? 0,
            claimAmount: item.warrantyClaimAmount != null ? `CNY ${this.formatAmount(item.warrantyClaimAmount)}` : '-',
            purchaseQty: item.purchaseQuantity ?? 0,
            purchaseAmount: item.purchaseAmount != null ? `CNY ${this.formatAmount(item.purchaseAmount)}` : '-',
            amountRatio: item.actualAmountRatio != null ? `${item.actualAmountRatio}%` : '-',
          }));
          this.categoryPagination.total = this.categoryAnalysis.length;
          this.categoryPagination.pageIndex = 1;
        }
      },
    });
  }

  renderMonthlyChart(): void {
    if (!this.monthlyChartRef) {
      return;
    }

    const qtyData = this.monthlyOverview.map(point => ({ month: point.month, qty: point.qty }));
    const amountData = this.monthlyOverview.map(point => ({ month: point.month, amount: point.amount }));

    if (!this.monthlyChartInstance) {
      this.monthlyChartInstance = new DualAxes(this.monthlyChartRef.nativeElement, {
        data: [qtyData, amountData],
        xField: 'month',
        yField: ['qty', 'amount'],
        autoFit: true,
        legend: false,
        geometryOptions: [
          {
            geometry: 'column',
            color: '#59A9D6',
            // 固定柱宽：min/max 同为 16px，不随容器宽度和数据条数变化
            // （DualAxes 会透传这两个约束到底层 G2 interval 几何）
            maxColumnWidth: 24,
          },
          {
            geometry: 'line',
            color: '#9E2896',
            lineStyle: { lineWidth: 2 },
            point: {
              size: 4,
              style: { lineWidth: 1, stroke: '#9E2896' },
            },
          },
        ] as any,
        xAxis: {
          label: {
            autoRotate: false,
          },
          line: { style: { stroke: '#b2b9c0' } },
          tickLine: null,
        },
        yAxis: {
          qty: {
            min: 0,
            grid: null,
            label: { style: { fill: '#575f67' } },
          },
          amount: {
            min: 0,
            grid: null,
            label: {
              style: { fill: '#575f67' },
              // 金额轴刻度加千分位
              formatter: (value: string) => this.formatAmount(value),
            },
          },
        },
        tooltip: {
          formatter: (datum: any) => {
            if (datum['qty'] != null) {
              return { name: '数量', value: `${datum['qty']} 个` };
            }
            return { name: '金额', value: `${Number(datum['amount']).toLocaleString()} 元` };
          },
        },
      });
      this.monthlyChartInstance.render();
    } else {
      this.monthlyChartInstance.changeData([qtyData, amountData]);
    }
  }

  getStatusColor(statusName: string): { color: string; bg: string } {
    switch (statusName) {
      case '索赔完成':
        return { color: '#009C5F', bg: '#E6F6EF' };
      case '拒绝索赔':
        return { color: '#ED0007', bg: '#FEEDED' };
      case '信息核对中':
        return { color: '#FF8726', bg: '#FFF6EE' };
      case '待索赔':
        return { color: '#71767C', bg: '#F2F3F4' };
      case '待批复':
        return { color: '#007BC0', bg: '#E6F2F9' };
      case '需返件':
        return { color: '#9E2896', bg: '#F8EEF6' };
      default:
        return { color: '#A0A0A0', bg: '#F2F3F4' };
    }
  }

  pageIndexChange(pageIndex: number): void {
    this.pagination.pageIndex = pageIndex;
    this.fetchWarrantyDetails();
  }

  pageSizeChange(pageSize: number): void {
    this.pagination.pageSize = pageSize;
    this.pagination.pageIndex = 1;
    this.fetchWarrantyDetails();
  }

  categoryPageIndexChange(pageIndex: number): void {
    this.categoryPagination.pageIndex = pageIndex;
  }

  categoryPageSizeChange(pageSize: number): void {
    this.categoryPagination.pageSize = pageSize;
    this.categoryPagination.pageIndex = 1;
  }

  /** 调用质保申请明细分页接口，刷新表格 */
  fetchWarrantyDetails(): void {
    const param: any = {};
    // 日期优先取明细筛选区间，未设置时回退到顶部全局区间
    const range = this.detailFilter.dateRange;
    if (range && range.length === 2) {
      param.startDate = moment(range[0]).startOf('month').format('YYYY-MM');
      param.endDate = moment(range[1]).endOf('month').format('YYYY-MM');
    } else if (this.dateRange && this.dateRange.length === 2) {
      param.startDate = moment(this.dateRange[0]).startOf('month').format('YYYY-MM');
      param.endDate = moment(this.dateRange[1]).endOf('month').format('YYYY-MM');
    }
    const claimNo = this.detailFilter.claimNo?.trim();
    if (claimNo) {
      param.claimNo = claimNo;
    }
    const materialNo = this.detailFilter.boschMaterialNo?.trim();
    if (materialNo) {
      param.boschMaterialNo = materialNo;
    }
    const body: any = {
      current: this.pagination.pageIndex,
      size: this.pagination.pageSize,
      param,
    };
    this.warrantyService.apiApplicationDetailPage(body).subscribe({
      next: (res) => {
        if (res.rtnData) {
          const page = res.rtnData;
          this.pagination.total = page.total ?? 0;
          this.warrantyDetails = (page.rows || []).map((item: any) => ({
            claimNumber: item.warrantyNo || '',
            submitDate: item.systemSubmitDate || '',
            qty: item.applyQuantity ?? 0,
            materialNumber: item.boschMaterialNo || '',
            claimAmount: item.claimAmount != null ? `CNY ${this.formatAmount(item.claimAmount)}` : '-',
            remark1: item.remark1 || '-',
            remark2: item.remark2 || '-',
            status: item.status || '',
            statusName: item.statusName || '',
          }));
        }
      },
    });
  }

  /** 调用信息速递分页接口 */
  fetchNews(): void {
    this.warrantyService.apiNewsPage({
      current: 1,
      size: -1,
      channelList: ['QMM'],
      isAdmin: false,
    }).subscribe({
      next: (res) => {
        if ((res.rtnCode === 0 || res.rtnCode === 10000) && res.rtnData) {
          const records = res.rtnData.records || [];
          this.newsList = records.map((item: any) => {
            const tagName = item.tagsVOList?.[0]?.tagName || '';
            return {
              date: item.createTime ? moment(item.createTime).format('YYYY年MM月DD日') : '',
              tag: tagName,
              tagColor: '#00619A',
              tagBg: '#E6F2F9',
              title: item.title || '',
              desc: item.content || '',
            };
          });
        }
      },
    });
  }
}
